import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ModalWindow, useLocalizeMessage } from 'libs.nucleus';
import { DataSettings } from './data_settings.component';
import { NO_DATA_FORMAT, isValidDataFormatForField } from '../../constants/data_view.constants';
import { useDataViews } from '../../hooks';
export const SettingsModal = ({ isOpen, onClose, onSave, dataView, isLoading = false, isReadOnly = false, }) => {
    const translate = useLocalizeMessage();
    const { updateDataView, isLoading: isApiLoading } = useDataViews();
    const dataSettingsRef = useRef(null);
    // Initialize items from dimensions and measures
    const initialItems = useMemo(() => {
        const items = [];
        // Add dimensions
        if (dataView.data?.dimensions) {
            Object.entries(dataView.data.dimensions).forEach(([key, dimension]) => {
                items.push({
                    id: `dimension-${key}`,
                    name: key,
                    type: dimension.type || 'string',
                    format: dimension.format || NO_DATA_FORMAT,
                    description: dimension.description || '',
                    displayName: dimension.title || '',
                    isVisible: dimension.public !== false,
                    category: 'Dimension',
                    originalName: key,
                });
            });
        }
        // Add measures
        if (dataView.data?.measures) {
            Object.entries(dataView.data.measures).forEach(([key, measure]) => {
                items.push({
                    id: `measure-${key}`,
                    name: key,
                    type: measure.type || 'number',
                    format: measure.format || NO_DATA_FORMAT,
                    description: measure.description || '',
                    displayName: measure.title || '',
                    isVisible: measure.public !== false,
                    category: 'Measure',
                    originalName: key,
                });
            });
        }
        return items;
    }, [dataView.data]);
    const [items, setItems] = useState(initialItems);
    // Reset items when dataView changes
    useEffect(() => {
        setItems(initialItems);
    }, [initialItems]);
    const itemsRef = useRef(items);
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);
    const handleItemsChange = useCallback((updatedItems) => {
        setItems(updatedItems);
        itemsRef.current = updatedItems;
    }, []);
    const handleClose = useCallback(() => {
        setItems(initialItems);
        onClose();
    }, [initialItems, onClose]);
    const handleSave = useCallback(async () => {
        try {
            // Flush any pending changes before saving
            if (dataSettingsRef.current) {
                dataSettingsRef.current.flushPendingChanges();
                // Wait a tick for state updates to propagate
                await new Promise((resolve) => setTimeout(resolve, 0));
            }
            // Get the latest items from ref (updated by flush)
            const itemsToSave = itemsRef.current;
            // Start with existing dimensions and measures, then update/rename them
            const updatedDimensions = { ...(dataView.data?.dimensions || {}) };
            const updatedMeasures = { ...(dataView.data?.measures || {}) };
            // Persist the format only when it is set and valid for the field ("None" / invalid -> omitted).
            // Validity is category-aware to match the Cube validator (measures reject `link`, etc.).
            const resolveFormat = (item) => item.format && isValidDataFormatForField(item.category, item.type, item.format) ? item.format : undefined;
            itemsToSave.forEach((item) => {
                if (item.category === 'Dimension') {
                    const originalKey = item.originalName;
                    const dimension = dataView.data?.dimensions?.[originalKey];
                    if (dimension) {
                        updatedDimensions[originalKey] = {
                            ...dimension,
                            title: item.displayName || dimension.title,
                            description: item.description,
                            public: item.isVisible,
                            type: item.type,
                            format: resolveFormat(item),
                        };
                    }
                }
                else if (item.category === 'Measure') {
                    const originalKey = item.originalName;
                    const measure = dataView.data?.measures?.[originalKey];
                    if (measure) {
                        updatedMeasures[originalKey] = {
                            ...measure,
                            title: item.displayName || measure.title,
                            description: item.description,
                            public: item.isVisible,
                            type: item.type,
                            format: resolveFormat(item),
                        };
                    }
                }
            });
            // Update the dataset - only update dimensions and measures
            const updatedView = await updateDataView(dataView.id, {
                data: {
                    ...dataView.data,
                    dimensions: updatedDimensions,
                    measures: updatedMeasures,
                },
            }, {
                replaceDimensions: true,
                replaceMeasures: true,
            });
            onSave(updatedView);
            handleClose();
        }
        catch (error) {
            // Error already handled in hook
        }
    }, [items, dataView, updateDataView, onSave, handleClose]);
    // Read-only datasets show a single Close action; editable datasets get Save + Cancel.
    const primaryActionButton = isReadOnly
        ? {
            label: translate('Close'),
            onClick: handleClose,
            disabled: isLoading || isApiLoading,
        }
        : {
            label: translate('Save'),
            onClick: handleSave,
            disabled: isLoading || isApiLoading,
        };
    const footerSecondaryActionButtons = isReadOnly
        ? undefined
        : [
            {
                label: translate('Cancel'),
                onClick: handleClose,
                disabled: isLoading || isApiLoading,
            },
        ];
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate('Data settings'), subtitle: dataView.name, isOpen: true, closeWindow: handleClose, footerPrimaryActionButton: primaryActionButton, footerSecondaryActionButtons: footerSecondaryActionButtons, width: 'full', children: _jsx(DataSettings, { ref: dataSettingsRef, items: items, onItemsChange: handleItemsChange, isReadOnly: isReadOnly }) }));
};
